import React from 'react';
import { Calendar, HelpCircle, Award, ShieldCheck, Heart } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const BOOKING_LINK = "https://system.easypractice.net/book/akupunktur-klinikken-acu-sense";

export default function About() {
  const faqs = [
    {
      question: "Kan jeg hjælpe dig?",
      answer: "Vores krop kan komme i ubalance og have brug for hjælp til at starte en helingsproces. Jeg har en stor overbevisning om, at kroppen som udgangspunkt er stærk og vil gøre hvad den kan for at kompensere for ubalancer. Jeg ser og behandler kroppen som en helhed og ikke kun dine ex knæsmerter eller forstoppelse. Længerevarende symptomer er ofte et tegn på en bagvedliggende årsag."
    },
    {
      question: "Hvor mange behandlinger skal der til?",
      answer: "Det er ingen hurtige genveje til et godt resultat. Et behandlingsforløb er en rigtig god mulighed for at følge dine symptomer til dørs samtidig med, at årsagen til dine problematikker bliver behandlet. Behandlingsforløbet bliver tilrettelagt individuelt. Flere faktorer kan påvirke antallet af behandlinger for at nå vores fælles aftalte mål fx: hvor lang tid du har haft dine symptomer, hvilke symptomer er til stede, er der flere ubalancer på samme tid, hvad er energiniveauet i kroppen. Et behandlingsforløb kan have positive og negative udsving. Det er en hel normal reaktion, der viser, at kroppen er i forandring."
    },
    {
      question: "Hvor ofte skal du have en behandling?",
      answer: "Kommer du med smerter kan det være en fordel at behandle min. 2 gange om ugen for at bryde de ubalancer, smerten har skabt i kroppen. Hyppige behandlinger vil forhindre, at kroppen når at ”falde” tilbage til det gamle mønster. Ved tilstande som for eksempel langvarige muskel-/nervesmerter, migræne, søvnproblematikker, stress eller depression bliver der sat gang i en dybere proces i kroppen. For at imødekomme processen, bliver behandlingsfrekvensen aftalt individuelt."
    }
  ];

  return (
    <>
      <Helmet>
        <title>Om klinikken | Klinik Acu-sense</title>
        <meta name="description" content="Læs om Klinik Acu-sense i Hammel og behandler Brian Hangaard. Brian er RAB-godkendt akupunktør og uddannet neurofysioterapeut med mange års erfaring." />
        <meta property="og:title" content="Om klinikken | Klinik Acu-sense" />
        <meta property="og:description" content="Læs om Klinik Acu-sense i Hammel og behandler Brian Hangaard. Brian er RAB-godkendt akupunktør og uddannet neurofysioterapeut med many års erfaring." />
        <link rel="canonical" href="https://www.acu-sense.dk/om" />
      </Helmet>

      {/* Page Header */}
      <section className="bg-[#F3F1EC] py-16 border-b border-[#E6E4DF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-4">
          <h1 className="font-serif text-4xl sm:text-5xl font-bold text-[#2C3531] tracking-tight">
            Om Klinikken & Brian
          </h1>
          <p className="text-base sm:text-lg text-[#5C6360] max-w-2xl mx-auto leading-relaxed">
            Mød din behandler og læs om min sundhedsfaglige tilgang til akupunktur og helhedsorienteret heling.
          </p>
        </div>
      </section>

      {/* Profile Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            {/* Image */}
            <div className="lg:col-span-5">
              <div className="relative">
                <div className="absolute -inset-4 rounded-3xl bg-[#E6E4DF] transform rotate-2 -z-10" />
                <img
                  src="https://assets.auxodev.dk/assets/2c4bccc2-b14e-4d92-a991-09327bae6f21/91ef7221-010c-4281-b8d0-6e03f8b95382.webp"
                  alt="Brian Hangaard - Klinik Acu-sense"
                  className="w-full h-[500px] object-cover rounded-2xl shadow-md border-4 border-white"
                />
              </div>
            </div>

            {/* Text Content */}
            <div className="lg:col-span-7 space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#8FA89B]/10 text-[#3D4F44] text-xs font-bold uppercase tracking-wider">
                <Award className="w-4 h-4" />
                RAB-godkendt akupunktør & neurofysioterapeut
              </div>
              
              <h2 className="font-serif text-3xl font-bold text-[#2C3531] tracking-tight">
                Mit navn er Brian Hangaard
              </h2>

              <div className="prose prose-stone text-sm sm:text-base text-[#5C6360] leading-relaxed space-y-4">
                <p>
                  Jeg er uddannet akupunktør (RAB godkendt) og fysioterapeut. Som uddannet akupunktør kan jeg behandle en lang række tilstande, symptomer og lidelser.
                </p>
                <p>
                  Mit specialer er smertebehandling, hovedpine, migræne, søvnforstyrrelser, fordøjelsesproblematikker og hedeture/overgangsalder.
                </p>
                <p>
                  Jeg behandler dine symptomer, men også årsagen til dine ubalancer. Behandlingen genskaber balancen og energien i din krop, så du ex kan vende tilbage til dit arbejde, genoptage din fritidsinteresse, få mere energi i hverdagen eller noget helt fjerde.
                </p>
                <p className="font-serif italic text-lg text-[#2C3531] pt-2 border-l-2 border-[#5F7464] pl-4">
                  "Jeg har en stor overbevisning om, at kroppen som udgangspunkt er stærk og vil gøre hvad den kan for at kompensere for ubalancer."
                </p>
              </div>

              {/* Badges */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
                <div className="flex items-start gap-3 p-4 rounded-xl bg-[#FAF9F6] border border-[#E6E4DF]">
                  <ShieldCheck className="w-5 h-5 text-[#5F7464] shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-bold text-[#2C3531]">RAB-godkendt</h4>
                    <p className="text-xs text-[#5C6360] leading-relaxed">Registreret Alternativ Behandler. Giver ret til tilskud fra Sygeforsikringen "danmark".</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 rounded-xl bg-[#FAF9F6] border border-[#E6E4DF]">
                  <Heart className="w-5 h-5 text-[#5F7464] shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-bold text-[#2C3531]">Neurofysioterapeut</h4>
                    <p className="text-xs text-[#5C6360] leading-relaxed">Kombinerer dyb neurologisk og anatomisk viden med akupunkturens helhedssyn.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-[#FAF9F6] border-t border-b border-[#E6E4DF]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12 space-y-4">
            <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#2C3531] tracking-tight">
              Spørgsmål jeg ofte får i klinikken
            </h2>
            <p className="text-sm sm:text-base text-[#5C6360]">
              Her finder du svar på nogle af de mest almindelige spørgsmål omkring akupunkturbehandling og forløb.
            </p>
          </div>

          <div className="space-y-6">
            {faqs.map((faq, idx) => (
              <div
                key={idx}
                className="bg-white p-6 sm:p-8 rounded-2xl border border-[#E6E4DF] shadow-sm space-y-3"
              >
                <h3 className="font-serif text-lg font-bold text-[#2C3531] flex items-start gap-3">
                  <HelpCircle className="w-5 h-5 text-[#5F7464] shrink-0 mt-1" />
                  <span>{faq.question}</span>
                </h3>
                <p className="text-sm sm:text-base text-[#5C6360] leading-relaxed pl-8">
                  {faq.answer}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Booking CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#2C3531]">
            Vil du have hjælp til at genfinde din krops balance?
          </h2>
          <p className="text-sm sm:text-base text-[#5C6360] max-w-xl mx-auto">
            Book en tid online allerede i dag, og lad mig hjælpe dig med at tilrettelægge et behandlingsforløb, der passer præcis til dig.
          </p>
          <div className="pt-4">
            <a
              href={BOOKING_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-8 py-4 rounded-full bg-[#5F7464] text-white font-medium tracking-wide hover:bg-[#4E6052] transition-colors duration-200 shadow-md"
            >
              <Calendar className="w-5 h-5 mr-2" />
              Bestil behandling online
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
